import { useEffect, useState, useRef } from 'react';
import styles from './css/Loading.module.css';

type LoadingSize = 'small' | 'medium' | 'large';
type LoadingVariant = 'overlay' | 'inline' | 'inlineDark';

type LoadingProps = {
    /** 로딩 표시 여부 */
    isLoading?: boolean;
    /** 로딩 메시지 */
    message?: string;
    /** 로딩 크기 */
    size?: LoadingSize;
    /** 로딩 변형 (overlay: 전체화면, inline: 인라인, inlineDark: 다크 인라인) */
    variant?: LoadingVariant;
    /** 프로그레스 바 표시 여부 */
    showProgress?: boolean;
    /** 프로그레스 값 (0-100, undefined면 indeterminate) */
    progress?: number;
    /** 최소 로딩 표시 시간 (ms) - 로딩이 완료되어도 이 시간만큼은 유지 */
    minDuration?: number;
    /** 페이드 아웃 시 콜백 */
    onFadeOutComplete?: () => void;
};

function Loading({
    isLoading = true,
    message = '로딩 중',
    size = 'medium',
    variant = 'overlay',
    showProgress = false,
    progress,
    minDuration = 0,
    onFadeOutComplete,
}: LoadingProps) {
    const [isVisible, setIsVisible] = useState(isLoading);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const loadingStartTime = useRef<number | null>(null);

    useEffect(() => {
        if (isLoading) {
            // 로딩 시작 시간 기록
            if (loadingStartTime.current === null) {
                loadingStartTime.current = Date.now();
            }
            setIsVisible(true);
            setIsFadingOut(false);
        } else if (isVisible) {
            // 최소 지속 시간 계산
            const elapsed = loadingStartTime.current ? Date.now() - loadingStartTime.current : 0;
            const remainingTime = Math.max(0, minDuration - elapsed);

            const startFadeOut = () => {
                setIsFadingOut(true);
                const fadeTimer = setTimeout(() => {
                    setIsVisible(false);
                    setIsFadingOut(false);
                    loadingStartTime.current = null;
                    onFadeOutComplete?.();
                }, 300);
                return fadeTimer;
            };

            if (remainingTime > 0) {
                // 최소 지속 시간이 남아있으면 대기 후 페이드 아웃
                const waitTimer = setTimeout(() => {
                    startFadeOut();
                }, remainingTime);
                return () => clearTimeout(waitTimer);
            } else {
                // 최소 지속 시간이 지났으면 바로 페이드 아웃
                const fadeTimer = startFadeOut();
                return () => clearTimeout(fadeTimer);
            }
        }
    }, [isLoading, isVisible, minDuration, onFadeOutComplete]);

    // 오버레이일 때 body 스크롤 방지
    useEffect(() => {
        if (variant === 'overlay' && isVisible) {
            document.body.style.overflow = 'hidden';
        } else if (variant === 'overlay') {
            document.body.style.overflow = 'unset';
        }
        return () => {
            if (variant === 'overlay') {
                document.body.style.overflow = 'unset';
            }
        };
    }, [isVisible, variant]);

    if (!isVisible) return null;

    const getOverlayClass = () => {
        const classes = [styles.overlay];
        
        if (size === 'small') classes.push(styles.small);
        if (size === 'large') classes.push(styles.large);
        if (variant === 'inline') classes.push(styles.inline);
        if (variant === 'inlineDark') classes.push(styles.inlineDark);
        if (isFadingOut) classes.push(styles.fadeOut);
        
        return classes.join(' ');
    };

    return (
        <div className={getOverlayClass()}>
            <div className={styles.content}>
                {/* 메인 로더 */}
                <div className={styles.loaderContainer}>
                    {/* 파티클 효과 */}
                    <div className={styles.particles}>
                        {/* <div className={styles.particle} /> */}
                        {/* <div className={styles.particle} /> */}
                        {/* <div className={styles.particle} />
                        <div className={styles.particle} />
                        <div className={styles.particle} />
                        <div className={styles.particle} /> */}
                    </div>
                    
                    {/* 스피너 링 */}
                    {/* <div className={styles.ringOuter} /> */}
                    <div className={styles.ringMiddle} />
                    <div className={styles.ringInner} />
                    
                    {/* 중앙 코어 */}
                    <div className={styles.core} />
                </div>

                {/* 텍스트 영역 */}
                <div className={styles.textContainer}>
                    {message && (
                        <span className={styles.message}>{message}</span>
                    )}
                    
                    {/* 로딩 도트 */}
                    <div className={styles.dots}>
                        <div className={styles.dot} />
                        <div className={styles.dot} />
                        <div className={styles.dot} />
                    </div>
                </div>

                {/* 프로그레스 바 */}
                {showProgress && (
                    <div className={styles.progressContainer}>
                        <div 
                            className={`${styles.progressBar} ${progress === undefined ? styles.progressIndeterminate : ''}`}
                            style={progress !== undefined ? { width: `${Math.min(100, Math.max(0, progress))}%` } : undefined}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default Loading;
export type { LoadingProps, LoadingSize, LoadingVariant };
